<script setup>
import { ref, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SgpAPI from 'dashboard/api/sgp';
import { normalizeBrazilPhone } from 'dashboard/helpers/phoneBrazil';

// Props
const props = defineProps({
  selectedContractId: {
    type: String,
    default: null,
  },
  identified: {
    type: Object,
    default: null,
  },
});

// Emits
const emit = defineEmits([
  'customerIdentified',
  'connectivityUpdated',
  'serviceOrdersUpdated',
  'preViabilitySuccess',
]);

// i18n
const { t: $t } = useI18n();

// Dialog visibility
const showIdentifyDialog = ref(false);
const showViabilityCepDialog = ref(false);
const showViabilityCtoDialog = ref(false);
const showPreRegistrationDialog = ref(false);
const showPreChooserDialog = ref(false);
const showScheduleInstallDialog = ref(false);
const showPhoneSearchDialog = ref(false);
const showPromiseDialog = ref(false);

// CEP helpers
const cepLookupLoading = ref(false);
const cepLookupError = ref('');
const cepAutocompleteLoading = ref(false);
const cepAutocompleteResults = ref([]);
const cepAutocompleteOpen = ref(false);
const cepAutocompleteFocused = ref(false);
let cepAutocompleteTimer = null;
const ctoCepLookupError = ref('');
const lastCepSearched = ref('');
const lastCtoCepSearched = ref('');
const numberInputRef = ref(null);
const geocodeLoading = ref(false);
const geocodeError = ref('');
const ctoErrors = ref({});
const ctoAutocompleteLoading = ref(false);
const ctoAutocompleteResults = ref([]);
const ctoAutocompleteOpen = ref(false);
const ctoAutocompleteFocused = ref(false);
let ctoAutocompleteTimer = null;
const manualCoords = ref(false);
const lastCoordsReverse = ref('');
const suppressReverse = ref(false);
let autoGeocodeTimer = null;
const lastAutoGeocodeKey = ref('');

// Dialog refs (to call .open())
const identifyRef = ref(null);
const cepRef = ref(null);
const ctoRef = ref(null);
const preRef = ref(null);
const preChooserRef = ref(null);
const scheduleRef = ref(null);
const phoneRef = ref(null);

// Forms
const identifyDocumento = ref('');
const cepForm = ref({
  cep: '',
  logradouro: '',
  bairro: '',
  cidade: '',
  uf: '',
});
const ctoForm = ref({
  cep: '',
  raio: '300',
  endereco: '',
  numero: '',
  bairro: '',
  latitude: '',
  longitude: '',
  coordenada: '',
  cidade: '',
  uf: '',
});
const phoneInput = ref('');
const promiseForm = ref({ data_promessa: '', enviar_sms: '0' });
const preViabilityResult = ref(null);

const preFlowSource = ref(null); // 'cep' | 'cto' | null
const lastPrePayload = ref(null);

// Submit loading flags
const submittingIdentify = ref(false);
const submittingCep = ref(false);
const submittingCto = ref(false);
const promiseSubmitting = ref(false);

// Results
const identifyResult = ref(null);
const viabilityCepResult = ref(null);
const viabilityCtoResult = ref(null);

function open(action) {
  if (action === 'identify') {
    showIdentifyDialog.value = true;
    nextTick(() => identifyRef.value?.open());
  }
  if (action === 'cep') {
    preFlowSource.value = 'cep';
    lastPrePayload.value = null;
    showViabilityCepDialog.value = true;
    nextTick(() => cepRef.value?.open());
  }
  if (action === 'cto') {
    preFlowSource.value = 'cto';
    lastPrePayload.value = null;
    showViabilityCtoDialog.value = true;
    nextTick(() => ctoRef.value?.open());
  }
  if (action === 'pre') {
    preViabilityResult.value = null;
    preFlowSource.value = null;
    lastPrePayload.value = null;
    showPreChooserDialog.value = false;
    emit('preViabilitySuccess', { payload: {}, result: null, manual: true });
    nextTick(() => preChooserRef.value?.close?.());
  }
  if (action === 'schedule') {
    showScheduleInstallDialog.value = true;
    nextTick(() => scheduleRef.value?.open());
  }
  if (action === 'phone') {
    showPhoneSearchDialog.value = true;
    nextTick(() => phoneRef.value?.open());
  }
  if (action === 'promise') {
    showPromiseDialog.value = true;
  }
}

function closeIdentifyDialog() {
  showIdentifyDialog.value = false;
  // Clear all form data and results
  identifyDocumento.value = '';
  identifyResult.value = null;
  nextTick(() => {
    identifyRef.value?.close?.();
  });
}

function resetIdentifyDialog() {
  // Clear all form data and results when dialog is closed
  identifyDocumento.value = '';
  identifyResult.value = null;
  showIdentifyDialog.value = false;
}

function choosePreFlow(source) {
  showPreChooserDialog.value = false;
  preFlowSource.value = source;

  if (source === 'cep') {
    showViabilityCepDialog.value = true;
    nextTick(() => {
      preChooserRef.value?.close?.();
      cepRef.value?.open?.();
    });
    return;
  }

  if (source === 'cto') {
    showViabilityCtoDialog.value = true;
    nextTick(() => {
      preChooserRef.value?.close?.();
      ctoRef.value?.open?.();
    });
    return;
  }

  if (source === 'manual') {
    emit('preViabilitySuccess', { payload: {}, result: null, manual: true });
    preFlowSource.value = null;
    nextTick(() => preChooserRef.value?.close?.());
  }
}

function resetCtoDialog() {
  // Clear all form data and results when dialog is closed
  ctoForm.value = {
    cep: '',
    raio: '300',
    endereco: '',
    numero: '',
    bairro: '',
    cidade: '',
    uf: '',
    latitude: '',
    longitude: '',
    coordenada: '',
  };
  viabilityCtoResult.value = null;
  ctoCepLookupError.value = '';
  lastCtoCepSearched.value = '';
  geocodeError.value = '';
  ctoErrors.value = {};
  ctoAutocompleteResults.value = [];
  ctoAutocompleteOpen.value = false;
  ctoAutocompleteFocused.value = false;
  if (ctoAutocompleteTimer) clearTimeout(ctoAutocompleteTimer);
  manualCoords.value = false;
  showViabilityCtoDialog.value = false;
  preFlowSource.value = null;
  lastPrePayload.value = null;
}

function resetCepDialog() {
  // Clear all form data and results when dialog is closed
  cepForm.value = { cep: '', logradouro: '', bairro: '', cidade: '', uf: '' };
  viabilityCepResult.value = null;
  cepLookupError.value = '';
  lastCepSearched.value = '';
  cepAutocompleteResults.value = [];
  cepAutocompleteOpen.value = false;
  cepAutocompleteFocused.value = false;
  if (cepAutocompleteTimer) clearTimeout(cepAutocompleteTimer);
  showViabilityCepDialog.value = false;
  preFlowSource.value = null;
  lastPrePayload.value = null;
}

function resetPreDialog() {
  ctoForm.value = {
    cep: '',
    raio: '300',
    endereco: '',
    numero: '',
    bairro: '',
    cidade: '',
    uf: '',
    latitude: '',
    longitude: '',
    coordenada: '',
  };
  preViabilityResult.value = null;
  ctoCepLookupError.value = '';
  lastCtoCepSearched.value = '';
  geocodeError.value = '';
  ctoErrors.value = {};
  ctoAutocompleteResults.value = [];
  ctoAutocompleteOpen.value = false;
  ctoAutocompleteFocused.value = false;
  if (ctoAutocompleteTimer) clearTimeout(ctoAutocompleteTimer);
  manualCoords.value = false;
  showPreRegistrationDialog.value = false;
  preFlowSource.value = null;
  lastPrePayload.value = null;
}

async function onIdentifyConfirm() {
  submittingIdentify.value = true;
  identifyResult.value = null;
  try {
    const { data } = await SgpAPI.identifyCustomer({
      cpfcnpj: identifyDocumento.value,
      limit: 1,
    });

    // Handle different response structures
    let customer = null;
    if (data?.paginacao && Array.isArray(data?.clientes)) {
      customer = data.clientes.length ? data.clientes[0] : null;
    } else if (Array.isArray(data)) {
      customer = data.length ? data[0] : null;
    } else if (data && typeof data === 'object' && !Array.isArray(data)) {
      customer = data;
    }

    identifyResult.value = customer || { notFound: true };
    if (customer) {
      // Close dialog immediately after successful identification
      closeIdentifyDialog();
      identifyDocumento.value = '';

      // Emit customer identified event
      emit('customerIdentified', customer);
    }
  } catch (e) {
    identifyResult.value = { error: true };
  } finally {
    submittingIdentify.value = false;
  }
}

async function onPromiseReleaseConfirm() {
  if (!props.identified || !props.selectedContractId) return;
  promiseSubmitting.value = true;
  try {
    const payload = {
      contrato: props.selectedContractId.toString(),
      data_promessa: promiseForm.value.data_promessa,
      enviar_sms: promiseForm.value.enviar_sms || '0',
      uracontato: 'chatwoot_sgp',
      uraIP: '127.0.0.1',
    };
    const response = await SgpAPI.promiseRelease(payload);

    // Check if successful (API returns { liberado: true/false })
    if (response.data && response.data.liberado) {
      showPromiseDialog.value = false;
      // Emit events to refresh connectivity and other data
      emit('connectivityUpdated');
      emit('serviceOrdersUpdated');
    } else {
      // Show error message from API response
      // Promise release failed
    }
  } catch (e) {
    // Promise release error
    // keep dialog open for corrections
  } finally {
    promiseSubmitting.value = false;
  }
}

async function onPhoneSearchConfirm(phoneInputValue) {
  const tel = normalizeBrazilPhone(phoneInputValue || '');
  if (!tel) return;
  try {
    const { data } = await SgpAPI.consultaCliente({ telefone: tel, limit: 1 });
    const customer =
      Array.isArray(data?.clientes) && data.clientes.length
        ? data.clientes[0]
        : null;

    identifyResult.value = customer || { notFound: true };
    if (customer) {
      emit('customerIdentified', customer);
    }
  } catch (e) {
    identifyResult.value = { error: true };
  }
}

async function onViabilityCepConfirm() {
  submittingCep.value = true;
  viabilityCepResult.value = null;
  try {
    const payload = { cep: (cepForm.value.cep || '').replace(/\D/g, '') };
    if (cepForm.value.logradouro) payload.logradouro = cepForm.value.logradouro;
    if (cepForm.value.bairro) payload.bairro = cepForm.value.bairro;
    if (preFlowSource.value === 'cep') lastPrePayload.value = payload;
    const { data } = await SgpAPI.viabilityByZip(payload);
    viabilityCepResult.value = data;
  } catch (e) {
    viabilityCepResult.value = { error: true };
  } finally {
    submittingCep.value = false;
  }
}

function proceedPreFromViability() {
  if (!preFlowSource.value) return;
  const result =
    preFlowSource.value === 'cto'
      ? viabilityCtoResult.value
      : viabilityCepResult.value;
  const payload = lastPrePayload.value || {};

  emit('preViabilitySuccess', {
    payload,
    result,
    source: preFlowSource.value,
  });

  showViabilityCtoDialog.value = false;
  showViabilityCepDialog.value = false;
  preFlowSource.value = null;
  lastPrePayload.value = null;
}

function refVal(maybeRef) {
  return maybeRef && typeof maybeRef === 'object' && 'value' in maybeRef
    ? maybeRef.value
    : maybeRef;
}

function setRefVal(maybeRef, val) {
  if (maybeRef && typeof maybeRef === 'object' && 'value' in maybeRef) {
    maybeRef.value = val;
  }
}

function markManualCoords() {
  manualCoords.value = true;
}

function clearCtoError(field) {
  if (!ctoErrors.value[field]) return;
  ctoErrors.value = { ...ctoErrors.value, [field]: '' };
}

function parseCoords(coordOrLat, lngMaybe) {
  let lat = coordOrLat;
  let lng = lngMaybe;

  if (typeof lat === 'string' && lat.includes(',')) {
    const parts = lat.split(',');
    [lat, lng] = [parts[0], parts[1]];
  }

  lat = lat?.toString()?.trim();
  lng = lng?.toString()?.trim();

  if (lat && lng) return { lat, lng };
  return null;
}

function coordsKey(lat, lng) {
  return `${lat},${lng}`;
}

function extractGeo(result) {
  if (!result || typeof result !== 'object') return null;
  const geo = result.geo || result.location || {};
  const lat =
    geo.lat ?? geo.latitude ?? result.lat ?? result.latitude ?? geo.lat;
  const lon =
    geo.lon ??
    geo.lng ??
    geo.longitude ??
    result.lon ??
    result.lng ??
    result.longitude;

  if (lat === undefined || lat === null || lat === '') return null;
  if (lon === undefined || lon === null || lon === '') return null;

  return { lat: lat.toString(), lon: lon.toString() };
}

function applyCoords(target, lat, lon) {
  if (!target || typeof target !== 'object') return;
  manualCoords.value = false;
  suppressReverse.value = true;
  if ('latitude' in target) target.latitude = lat;
  if ('longitude' in target) target.longitude = lon;
  if ('coordenada' in target) target.coordenada = `${lat},${lon}`;
  nextTick(() => {
    suppressReverse.value = false;
  });
}

function canGeocode(target) {
  if (!target || typeof target !== 'object') return false;
  const logradouro = (target.endereco || target.logradouro || '').trim();
  const numero = (target.numero || '').toString().trim();
  const cep = (target.cep || '').replace(/\D+/g, '');
  const hasLocal = (target.cidade && target.uf) || cep;
  const hasCoords =
    target.coordenada?.trim() ||
    (target.latitude?.toString().trim() && target.longitude?.toString().trim());

  return Boolean(logradouro && numero && hasLocal && !hasCoords);
}

function buildAutoGeocodeKey(target) {
  if (!canGeocode(target)) return null;
  const logradouro = (target.endereco || target.logradouro || '').trim();
  const numero = (target.numero || '').toString().trim();
  const bairro = (target.bairro || '').trim();
  const cidade = (target.cidade || '').trim();
  const uf = (target.uf || '').trim();
  const cep = (target.cep || '').replace(/\D+/g, '');
  return [logradouro, numero, bairro, cidade, uf, cep].join('|');
}

function isCtoViable(result) {
  return (
    (Array.isArray(result) && result[0]?.SGPMaps?.length) ||
    result?.viabilidade === true
  );
}

function isCepViable(result) {
  return result?.viabilidade === true;
}

function canUseCepAutocomplete() {
  const cleanCep = (cepForm.value.cep || '').replace(/\D+/g, '');
  return cleanCep.length !== 8;
}

function handleCepAddressFocus() {
  cepAutocompleteFocused.value = true;
  cepAutocompleteOpen.value =
    canUseCepAutocomplete() && cepAutocompleteResults.value.length > 0;
}

function handleCepAddressBlur() {
  cepAutocompleteFocused.value = false;
  setTimeout(() => {
    cepAutocompleteOpen.value = false;
  }, 150);
}

function closeCtoAutocomplete() {
  ctoAutocompleteOpen.value = false;
}

function canUseCtoAutocomplete() {
  const cleanCep = (ctoForm.value.cep || '').replace(/\D+/g, '');
  return cleanCep.length !== 8;
}

function handleCtoAddressFocus() {
  ctoAutocompleteFocused.value = true;
  ctoAutocompleteOpen.value =
    canUseCtoAutocomplete() && ctoAutocompleteResults.value.length > 0;
}

function handleCtoAddressBlur() {
  ctoAutocompleteFocused.value = false;
  setTimeout(() => {
    closeCtoAutocomplete();
  }, 150);
}

function selectCtoAddressSuggestion(item) {
  if (!item) return;
  ctoForm.value.endereco = item.logradouro || ctoForm.value.endereco;
  if (!ctoForm.value.bairro && item.bairro) ctoForm.value.bairro = item.bairro;
  if (!ctoForm.value.cidade && item.localidade) {
    ctoForm.value.cidade = item.localidade;
  }
  if (!ctoForm.value.uf && item.uf) ctoForm.value.uf = item.uf;
  if (!ctoForm.value.cep && item.cep) ctoForm.value.cep = item.cep;
  ctoAutocompleteOpen.value = false;
}

function selectCepAddressSuggestion(item) {
  if (!item) return;
  cepForm.value.logradouro = item.logradouro || cepForm.value.logradouro;
  if (!cepForm.value.bairro && item.bairro) cepForm.value.bairro = item.bairro;
  if (!cepForm.value.cidade && item.localidade) {
    cepForm.value.cidade = item.localidade;
  }
  if (!cepForm.value.uf && item.uf) cepForm.value.uf = item.uf;
  if (!cepForm.value.cep && item.cep) cepForm.value.cep = item.cep;
  cepAutocompleteOpen.value = false;
}

async function fetchCepAddressAutocomplete(query) {
  try {
    cepAutocompleteLoading.value = true;
    const { data } = await SgpAPI.cepAutocomplete({
      q: query,
      uf: cepForm.value.uf,
      localidade: cepForm.value.cidade,
      bairro: cepForm.value.bairro,
      limit: 8,
    });
    cepAutocompleteResults.value = data?.results || [];
    cepAutocompleteOpen.value =
      cepAutocompleteFocused.value &&
      canUseCepAutocomplete() &&
      cepAutocompleteResults.value.length > 0;
  } catch (e) {
    cepAutocompleteResults.value = [];
    cepAutocompleteOpen.value = false;
  } finally {
    cepAutocompleteLoading.value = false;
  }
}

async function fetchCtoAddressAutocomplete(query) {
  try {
    ctoAutocompleteLoading.value = true;
    const { data } = await SgpAPI.cepAutocomplete({
      q: query,
      uf: ctoForm.value.uf,
      localidade: ctoForm.value.cidade,
      bairro: ctoForm.value.bairro,
      limit: 8,
    });
    ctoAutocompleteResults.value = data?.results || [];
    ctoAutocompleteOpen.value =
      ctoAutocompleteFocused.value &&
      canUseCtoAutocomplete() &&
      ctoAutocompleteResults.value.length > 0;
  } catch (e) {
    ctoAutocompleteResults.value = [];
    ctoAutocompleteOpen.value = false;
  } finally {
    ctoAutocompleteLoading.value = false;
  }
}

async function fetchAndFillCep(target, cleanCep, errorRef, lastRef) {
  const lastVal = refVal(lastRef);
  if (!cleanCep || cleanCep.length !== 8 || cleanCep === lastVal) return;
  // Garanta que temos um objeto mutável para preencher; evita crash se algo
  // inesperado enviar string ou valor primitivo
  if (!target || typeof target !== 'object') {
    setRefVal(errorRef, $t('SGP.PRE.CEP_ERROR'));
    return;
  }
  try {
    cepLookupLoading.value = true;
    setRefVal(errorRef, '');
    const { data } = await SgpAPI.cepLookup(cleanCep);
    const result = data?.primary || data?.results?.[0];
    if (!result) {
      setRefVal(errorRef, $t('SGP.PRE.CEP_NOT_FOUND'));
      return;
    }
    setRefVal(lastRef, cleanCep);
    target.cep = result.cep || cleanCep;
    if ('logradouro' in target)
      target.logradouro = result.logradouro || target.logradouro;
    if ('endereco' in target)
      target.endereco = result.logradouro || target.endereco;
    if ('bairro' in target) target.bairro = result.bairro || target.bairro;
    if ('cidade' in target) target.cidade = result.localidade || target.cidade;
    if ('uf' in target) target.uf = result.uf || target.uf;

    // após preencher endereço, mover foco para número se disponível
    await nextTick();
    numberInputRef.value?.focus?.();
  } catch (e) {
    const status = e.response?.status;
    setRefVal(
      errorRef,
      status === 404 ? $t('SGP.PRE.CEP_NOT_FOUND') : $t('SGP.PRE.CEP_ERROR')
    );
  } finally {
    cepLookupLoading.value = false;
  }
}

async function geocodeFromAddress(targetArg = ctoForm.value) {
  // targetArg pode vir como ref ou objeto simples; fallback para ctoForm
  const target = refVal(targetArg) || targetArg || ctoForm.value;
  if (!target || typeof target !== 'object') {
    geocodeError.value = $t('SGP.PRE.GEO_MISSING_ADDRESS');
    return;
  }

  const logradouro = (target.endereco || target.logradouro || '').trim();
  if (!logradouro || logradouro.length < 2) {
    geocodeError.value = $t('SGP.PRE.GEO_MISSING_ADDRESS');
    return;
  }
  const numero = (target.numero || '').toString().trim();
  if (!numero) {
    geocodeError.value = $t('SGP.PRE.GEO_MISSING_NUMBER');
    return;
  }

  geocodeLoading.value = true;
  geocodeError.value = '';
  try {
    const params = {
      logradouro,
      numero: target.numero,
      bairro: target.bairro,
      localidade: target.cidade,
      uf: target.uf,
      geo: true,
      limit: 1,
      offset: 0,
    };
    if (!numero && params.uf && params.localidade) params.strict = true;

    const { data } = await SgpAPI.cepSearch(params);
    const result = data?.results?.[0];
    const geo = extractGeo(result);
    if (geo) {
      applyCoords(target, geo.lat, geo.lon);
      return;
    }

    geocodeError.value = $t('SGP.PRE.GEO_ERROR');
  } catch (e) {
    geocodeError.value = e.response?.data?.error || $t('SGP.PRE.GEO_ERROR');
  } finally {
    geocodeLoading.value = false;
  }
}

function scheduleAutoGeocode(target) {
  const key = buildAutoGeocodeKey(target);
  if (!key || key === lastAutoGeocodeKey.value || geocodeLoading.value) return;
  if (autoGeocodeTimer) clearTimeout(autoGeocodeTimer);
  autoGeocodeTimer = setTimeout(() => {
    lastAutoGeocodeKey.value = key;
    geocodeFromAddress(target);
  }, 500);
}

async function reverseGeocodeFromCoords(coordsArg) {
  const coords = coordsArg || parseCoords(ctoForm.value.coordenada);
  const finalCoords =
    coords || parseCoords(ctoForm.value.latitude, ctoForm.value.longitude);

  if (!finalCoords) return;

  const key = coordsKey(finalCoords.lat, finalCoords.lng);
  if (key === lastCoordsReverse.value) return;
  lastCoordsReverse.value = key;

  geocodeLoading.value = true;
  geocodeError.value = '';
  try {
    const { data } = await SgpAPI.cepReverse({
      lat: finalCoords.lat,
      lon: finalCoords.lng,
      radius_m: 2000,
      limit: 1,
    });
    const result = data?.results?.[0];
    let primary = result?.primary;

    if (!primary && result?.cep) {
      const { data: cepData } = await SgpAPI.cepLookup(result.cep);
      primary = cepData?.primary || cepData?.results?.[0];
    }

    if (!primary) {
      geocodeError.value = $t('SGP.PRE.GEO_ERROR');
      return;
    }

    ctoForm.value.endereco = primary.logradouro || ctoForm.value.endereco;
    ctoForm.value.bairro = primary.bairro || ctoForm.value.bairro;
    ctoForm.value.cidade = primary.localidade || ctoForm.value.cidade;
    ctoForm.value.uf = primary.uf || ctoForm.value.uf;
    ctoForm.value.cep = primary.cep || ctoForm.value.cep;
  } catch (e) {
    geocodeError.value = $t('SGP.PRE.GEO_ERROR');
  } finally {
    geocodeLoading.value = false;
  }
}

async function onViabilityCtoConfirm() {
  ctoErrors.value = {};
  viabilityCtoResult.value = null;
  try {
    const cleanCep = (ctoForm.value.cep || '').replace(/\D+/g, '');
    const numero = (ctoForm.value.numero || '').toString().trim();

    if (!numero) {
      ctoErrors.value = { numero: $t('SGP.ERRORS.REQUIRED') };
      return;
    }

    // Validar se pelo menos um filtro de localização foi fornecido
    if (
      !cleanCep &&
      !ctoForm.value.endereco &&
      !ctoForm.value.bairro &&
      !ctoForm.value.latitude &&
      !ctoForm.value.longitude &&
      !ctoForm.value.coordenada
    ) {
      viabilityCtoResult.value = {
        error: true,
        message: $t('SGP.ERRORS.VIABILITY_FILTER_REQUIRED'),
      };
      return;
    }

    // Validar raio obrigatório
    if (!ctoForm.value.raio) {
      viabilityCtoResult.value = {
        error: true,
        message: $t('SGP.ERRORS.RADIUS_REQUIRED'),
      };
      return;
    }

    submittingCto.value = true;

    const payload = {
      raio: ctoForm.value.raio,
    };

    // Adicionar apenas campos preenchidos
    if (cleanCep) payload.cep = cleanCep;
    if (ctoForm.value.endereco?.trim())
      payload.endereco = ctoForm.value.endereco.trim();
    if (ctoForm.value.numero?.trim())
      payload.numero = ctoForm.value.numero.trim();
    if (ctoForm.value.bairro?.trim())
      payload.bairro = ctoForm.value.bairro.trim();
    if (ctoForm.value.cidade?.trim())
      payload.cidade = ctoForm.value.cidade.trim();
    if (ctoForm.value.uf?.trim()) payload.uf = ctoForm.value.uf.trim();

    let coord =
      ctoForm.value.coordenada?.trim() ||
      (ctoForm.value.latitude && ctoForm.value.longitude
        ? `${ctoForm.value.latitude},${ctoForm.value.longitude}`
        : '');

    if (!coord && canGeocode(ctoForm.value)) {
      await geocodeFromAddress(ctoForm.value);
      coord =
        ctoForm.value.coordenada?.trim() ||
        (ctoForm.value.latitude && ctoForm.value.longitude
          ? `${ctoForm.value.latitude},${ctoForm.value.longitude}`
          : '');
    }
    if (coord) payload.coordenada = coord;

    if (preFlowSource.value === 'cto') {
      lastPrePayload.value = payload;
    }

    const { data } = await SgpAPI.viabilityByCto(payload);
    viabilityCtoResult.value = data;
  } catch (e) {
    // Tratar diferentes tipos de erro
    if (e.response?.status === 422) {
      viabilityCtoResult.value = {
        error: true,
        message: e.response?.data?.msg || $t('SGP.ERRORS.INVALID_DATA'),
      };
    } else {
      viabilityCtoResult.value = { error: true };
    }
  } finally {
    submittingCto.value = false;
  }
}

watch(
  () => cepForm.value.cep,
  newVal => {
    const clean = (newVal || '').replace(/\D+/g, '');
    if (clean.length === 8) {
      cepAutocompleteResults.value = [];
      cepAutocompleteOpen.value = false;
      fetchAndFillCep(cepForm.value, clean, cepLookupError, lastCepSearched);
    }
  }
);

watch(
  () => cepForm.value.logradouro,
  newVal => {
    const query = (newVal || '').trim();
    if (!cepAutocompleteFocused.value || !canUseCepAutocomplete()) {
      if (cepAutocompleteTimer) clearTimeout(cepAutocompleteTimer);
      cepAutocompleteResults.value = [];
      cepAutocompleteOpen.value = false;
      return;
    }
    if (query.length < 4) {
      if (cepAutocompleteTimer) clearTimeout(cepAutocompleteTimer);
      cepAutocompleteResults.value = [];
      cepAutocompleteOpen.value = false;
      return;
    }
    if (cepAutocompleteTimer) clearTimeout(cepAutocompleteTimer);
    cepAutocompleteTimer = setTimeout(() => {
      fetchCepAddressAutocomplete(query);
    }, 400);
  }
);

watch(
  () => ctoForm.value.endereco,
  newVal => {
    const query = (newVal || '').trim();
    if (!ctoAutocompleteFocused.value || !canUseCtoAutocomplete()) {
      ctoAutocompleteResults.value = [];
      ctoAutocompleteOpen.value = false;
      return;
    }
    if (query.length < 4) {
      ctoAutocompleteResults.value = [];
      ctoAutocompleteOpen.value = false;
      return;
    }
    if (ctoAutocompleteTimer) clearTimeout(ctoAutocompleteTimer);
    ctoAutocompleteTimer = setTimeout(() => {
      fetchCtoAddressAutocomplete(query);
    }, 400);
  }
);

watch(
  () => ctoForm.value.cep,
  newVal => {
    const clean = (newVal || '').replace(/\D+/g, '');
    if (clean.length === 8) {
      ctoAutocompleteResults.value = [];
      ctoAutocompleteOpen.value = false;
    }
    if (clean.length === 8) {
      fetchAndFillCep(
        ctoForm.value,
        clean,
        ctoCepLookupError,
        lastCtoCepSearched
      );
    }
  }
);

watch(
  () => [
    ctoForm.value.numero,
    ctoForm.value.endereco,
    ctoForm.value.bairro,
    ctoForm.value.cidade,
    ctoForm.value.uf,
    ctoForm.value.cep,
  ],
  () => {
    scheduleAutoGeocode(ctoForm.value);
  }
);

watch(
  () => ctoForm.value.coordenada,
  newVal => {
    if (suppressReverse.value) return;
    const hasCoords = newVal?.toString().trim();
    const hasLat = ctoForm.value.latitude?.toString().trim();
    const hasLng = ctoForm.value.longitude?.toString().trim();
    if (!hasCoords && !hasLat && !hasLng) {
      manualCoords.value = false;
      return;
    }
    if (manualCoords.value) return;
    const coords = parseCoords(newVal);
    if (coords) reverseGeocodeFromCoords(coords);
  }
);

watch(
  () => [ctoForm.value.latitude, ctoForm.value.longitude],
  ([lat, lng]) => {
    if (suppressReverse.value) return;
    const hasLat = lat?.toString().trim();
    const hasLng = lng?.toString().trim();
    const hasCoord = ctoForm.value.coordenada?.toString().trim();
    if (!hasLat && !hasLng && !hasCoord) {
      manualCoords.value = false;
      return;
    }
    if (manualCoords.value) return;
    const coords = parseCoords(lat, lng);
    if (coords) reverseGeocodeFromCoords(coords);
  }
);

async function onPreRegistrationConfirm() {
  ctoErrors.value = {};
  preViabilityResult.value = null;
  const numero = (ctoForm.value.numero || '').toString().trim();
  if (!numero) {
    ctoErrors.value = { numero: $t('SGP.ERRORS.REQUIRED') };
    return;
  }
  submittingCto.value = true;
  try {
    const payload = { raio: ctoForm.value.raio || '300' };
    const cleanCep = (ctoForm.value.cep || '').replace(/\D/g, '');
    if (cleanCep) payload.cep = cleanCep;
    if (ctoForm.value.endereco?.trim())
      payload.endereco = ctoForm.value.endereco.trim();
    if (ctoForm.value.numero?.trim())
      payload.numero = ctoForm.value.numero.trim();
    if (ctoForm.value.bairro?.trim())
      payload.bairro = ctoForm.value.bairro.trim();

    let coord =
      ctoForm.value.coordenada?.trim() ||
      (ctoForm.value.latitude && ctoForm.value.longitude
        ? `${ctoForm.value.latitude},${ctoForm.value.longitude}`
        : '');

    if (!coord && canGeocode(ctoForm.value)) {
      await geocodeFromAddress(ctoForm.value);
      coord =
        ctoForm.value.coordenada?.trim() ||
        (ctoForm.value.latitude && ctoForm.value.longitude
          ? `${ctoForm.value.latitude},${ctoForm.value.longitude}`
          : '');
    }
    if (coord) payload.coordenada = coord;

    const { data } = await SgpAPI.viabilityByCto(payload);
    preViabilityResult.value = data;

    const viable =
      (Array.isArray(data) && data[0]?.SGPMaps?.length) ||
      data?.viabilidade === true;
    if (viable) {
      emit('preViabilitySuccess', { payload, result: data });
      showPreRegistrationDialog.value = false;
    }
  } catch (e) {
    preViabilityResult.value = { error: true };
  } finally {
    submittingCto.value = false;
  }
}

// Expose the open method
defineExpose({
  open,
});
</script>

<template>
  <div>
    <Dialog
      v-if="showPreChooserDialog"
      ref="preChooserRef"
      :is-loading="false"
      @close="() => (showPreChooserDialog = false)"
    >
      <template #title>{{ $t('SGP.PRE_FLOW.TITLE') }}</template>
      <div class="flex flex-col gap-3 text-sm text-n-slate-12">
        <p>{{ $t('SGP.PRE_FLOW.DESCRIPTION') }}</p>
        <div class="flex flex-col gap-2">
          <button
            type="button"
            class="w-full rounded border border-n-weak px-3 py-2 text-left hover:border-n-slate-10"
            @click="choosePreFlow('cep')"
          >
            {{ $t('SGP.PRE_FLOW.VIABILITY_CEP') }}
          </button>
          <button
            type="button"
            class="w-full rounded border border-n-weak px-3 py-2 text-left hover:border-n-slate-10"
            @click="choosePreFlow('cto')"
          >
            {{ $t('SGP.PRE_FLOW.VIABILITY_CTO') }}
          </button>
          <button
            type="button"
            class="w-full rounded border border-n-weak px-3 py-2 text-left hover:border-n-slate-10"
            @click="choosePreFlow('manual')"
          >
            {{ $t('SGP.PRE_FLOW.MANUAL') }}
          </button>
        </div>
      </div>
    </Dialog>

    <Dialog
      v-if="showIdentifyDialog"
      ref="identifyRef"
      :is-loading="submittingIdentify"
      @confirm="onIdentifyConfirm"
      @close="resetIdentifyDialog"
    >
      <template #title>{{ $t('SGP.DIALOGS.IDENTIFY.TITLE') }}</template>
      <div class="flex flex-col gap-3">
        <Input
          v-model="identifyDocumento"
          :label="$t('SGP.FORMS.DOCUMENT')"
          :placeholder="$t('SGP.FORMS.DOCUMENT_PLACEHOLDER')"
        />
        <div v-if="identifyResult">
          <div v-if="identifyResult.error" class="text-sm text-n-ruby-11">
            {{ $t('SGP.RESULTS.ERROR_GENERIC') }}
          </div>
          <div
            v-else-if="identifyResult.notFound"
            class="text-sm text-n-slate-11"
          >
            {{ $t('SGP.RESULTS.IDENTIFY_NOT_FOUND') }}
          </div>
          <div v-else class="text-sm text-n-slate-12">
            {{ identifyResult.nome }} {{ $t('SGP.SYMBOLS.DASH') }}
            {{
              $t('SGP.RESULTS.CONTRACTS_COUNT', {
                count: identifyResult.contratos
                  ? identifyResult.contratos.length
                  : 0,
              })
            }}
          </div>
        </div>
      </div>
    </Dialog>

    <Dialog
      v-if="showPromiseDialog"
      :is-loading="promiseSubmitting"
      @confirm="onPromiseReleaseConfirm"
      @close="() => (showPromiseDialog = false)"
    >
      <template #title>{{ $t('SGP.DIALOGS.PROMISE_RELEASE.TITLE') }}</template>
      <div class="flex flex-col gap-3">
        <div class="bg-n-solid-2/30 p-3 rounded-md">
          <p class="text-xs text-n-slate-11 mb-1">
            {{ $t('SGP.DIALOGS.PROMISE_RELEASE.SELECTED_CONTRACT') }}
          </p>
          <p class="text-sm font-medium text-n-slate-12">
            {{ $t('SGP.SYMBOLS.HASH')
            }}{{
              selectedContractId ||
              $t('SGP.DIALOGS.PROMISE_RELEASE.NO_CONTRACT')
            }}
          </p>
        </div>
        <Input
          v-model="promiseForm.data_promessa"
          :label="$t('SGP.DIALOGS.PROMISE_RELEASE.PROMISE_DATE')"
          type="date"
          :min="new Date().toISOString().split('T')[0]"
        />
        <div class="flex items-center gap-2">
          <input
            id="enviar_sms"
            v-model="promiseForm.enviar_sms"
            type="checkbox"
            true-value="1"
            false-value="0"
            class="rounded border-n-weak"
          />
          <label for="enviar_sms" class="text-sm text-n-slate-12">
            {{ $t('SGP.DIALOGS.PROMISE_RELEASE.SEND_SMS') }}
          </label>
        </div>
      </div>
    </Dialog>

    <Dialog
      v-if="showViabilityCepDialog"
      ref="cepRef"
      :is-loading="submittingCep"
      @confirm="onViabilityCepConfirm"
      @close="resetCepDialog"
    >
      <template #title>{{ $t('SGP.DIALOGS.VIABILITY_CEP.TITLE') }}</template>
      <div class="flex flex-col gap-3">
        <Input
          v-model="cepForm.cep"
          :label="$t('SGP.FORMS.CEP')"
          placeholder="00000-000"
          @blur="
            () =>
              fetchAndFillCep(
                cepForm.value,
                (cepForm.cep || '').replace(/\D+/g, ''),
                cepLookupError,
                lastCepSearched
              )
          "
        />
        <div class="relative">
          <Input
            v-model="cepForm.logradouro"
            :label="`${$t('SGP.FORMS.ADDRESS')} (${$t('SGP.FORMS.OPTIONAL')})`"
            @focus="handleCepAddressFocus"
            @blur="handleCepAddressBlur"
          />
          <ul
            v-if="cepAutocompleteOpen"
            class="absolute z-50 mt-1 w-full max-h-52 overflow-auto rounded-xl bg-n-alpha-3 backdrop-blur-[100px] outline outline-1 outline-n-container shadow-lg text-sm"
          >
            <template v-if="cepAutocompleteLoading">
              <li class="px-3 py-2 text-n-slate-11">
                {{ $t('SGP.LOADING') }}
              </li>
            </template>
            <template v-else>
              <li
                v-for="item in cepAutocompleteResults"
                :key="`${item.cep}-${item.logradouro}-${item.bairro}`"
                class="cursor-pointer px-3 py-2 hover:bg-n-alpha-2"
                @mousedown.prevent="selectCepAddressSuggestion(item)"
              >
                <div class="text-n-slate-12">
                  {{ item.logradouro }}
                </div>
                <div class="text-xs text-n-slate-11">
                  {{ item.bairro }}
                  {{ $t('SGP.SYMBOLS.BULLET') }}
                  {{ item.localidade }}{{ $t('SGP.SYMBOLS.SLASH')
                  }}{{ item.uf }}
                  {{ $t('SGP.SYMBOLS.BULLET') }}
                  {{ $t('SGP.FORMS.CEP') }} {{ item.cep }}
                </div>
              </li>
            </template>
          </ul>
        </div>
        <Input
          v-model="cepForm.bairro"
          :label="`${$t('SGP.FORMS.DISTRICT')} (${$t('SGP.FORMS.OPTIONAL')})`"
        />
        <div class="grid grid-cols-2 gap-2">
          <Input
            v-model="cepForm.cidade"
            :label="`${$t('SGP.FORMS.CITY')} (${$t('SGP.FORMS.OPTIONAL')})`"
          />
          <Input
            v-model="cepForm.uf"
            :label="`${$t('SGP.FORMS.STATE')} (${$t('SGP.FORMS.OPTIONAL')})`"
          />
        </div>
        <div v-if="cepLookupError" class="text-xs text-n-ruby-11">
          {{ cepLookupError }}
        </div>
        <div v-if="viabilityCepResult">
          <div v-if="viabilityCepResult.error" class="text-sm text-n-ruby-11">
            {{ $t('SGP.RESULTS.ERROR_GENERIC') }}
          </div>
          <div v-else class="text-sm text-n-slate-12">
            {{
              viabilityCepResult.viabilidade
                ? $t('SGP.RESULTS.VIABILITY_OK')
                : $t('SGP.RESULTS.VIABILITY_NOK')
            }}
            <div
              v-if="preFlowSource === 'cep' && isCepViable(viabilityCepResult)"
              class="mt-2"
            >
              <button
                type="button"
                class="px-3 py-2 rounded bg-n-brand text-white text-sm hover:opacity-90"
                @click="proceedPreFromViability"
              >
                {{ $t('SGP.PRE_FLOW.CONTINUE_PRE') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>

    <Dialog
      v-if="showPhoneSearchDialog"
      ref="phoneRef"
      @confirm="() => onPhoneSearchConfirm(phoneInput)"
      @close="() => (showPhoneSearchDialog = false)"
    >
      <template #title>{{ $t('SGP.ACTIONS.SEARCH_PHONE') }}</template>
      <div class="flex flex-col gap-3">
        <Input
          v-model="phoneInput"
          :label="$t('SGP.FORMS.PHONE')"
          :placeholder="$t('SGP.FORMS.PHONE_PLACEHOLDER')"
          type="tel"
        />
      </div>
    </Dialog>

    <Dialog
      v-if="showViabilityCtoDialog"
      ref="ctoRef"
      :is-loading="submittingCto"
      @confirm="onViabilityCtoConfirm"
      @close="resetCtoDialog"
    >
      <template #title>{{ $t('SGP.DIALOGS.VIABILITY_CTO.TITLE') }}</template>
      <div class="flex flex-col gap-3">
        <Input
          v-model="ctoForm.cep"
          :label="$t('SGP.FORMS.CEP')"
          placeholder="00000-000"
          @blur="
            () =>
              fetchAndFillCep(
                ctoForm.value,
                (ctoForm.cep || '').replace(/\D+/g, ''),
                ctoCepLookupError,
                lastCtoCepSearched
              )
          "
        />
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">{{
            $t('SGP.FORMS.RADIUS')
          }}</label>
          <select
            v-model="ctoForm.raio"
            class="w-full !rounded !bg-n-surface-1 !px-3 !py-2 !text-sm text-n-slate-12 !outline-n-weak hover:!outline-n-slate-6 focus:!outline-n-slate-7 !mb-0 !h-auto"
          >
            <option value="100">{{ $t('SGP.DISTANCE_100M') }}</option>
            <option value="300">{{ $t('SGP.DISTANCE_300M') }}</option>
            <option value="500">{{ $t('SGP.DISTANCE_500M') }}</option>
            <option value="1000">{{ $t('SGP.DISTANCE_1000M') }}</option>
          </select>
        </div>
        <div class="relative">
          <Input
            v-model="ctoForm.endereco"
            :label="`${$t('SGP.FORMS.ADDRESS')} (${$t('SGP.FORMS.OPTIONAL')})`"
            @focus="handleCtoAddressFocus"
            @blur="handleCtoAddressBlur"
          />
          <ul
            v-if="ctoAutocompleteOpen"
            class="absolute z-50 mt-1 w-full max-h-52 overflow-auto rounded-xl bg-n-alpha-3 backdrop-blur-[100px] outline outline-1 outline-n-container shadow-lg text-sm"
          >
            <template v-if="ctoAutocompleteLoading">
              <li class="px-3 py-2 text-n-slate-11">
                {{ $t('SGP.LOADING') }}
              </li>
            </template>
            <template v-else>
              <li
                v-for="item in ctoAutocompleteResults"
                :key="`${item.cep}-${item.logradouro}-${item.bairro}`"
                class="cursor-pointer px-3 py-2 hover:bg-n-alpha-2"
                @mousedown.prevent="selectCtoAddressSuggestion(item)"
              >
                <div class="text-n-slate-12">
                  {{ item.logradouro }}
                </div>
                <div class="text-xs text-n-slate-11">
                  {{ item.bairro }}
                  {{ $t('SGP.SYMBOLS.BULLET') }}
                  {{ item.localidade }}{{ $t('SGP.SYMBOLS.SLASH')
                  }}{{ item.uf }}
                  {{ $t('SGP.SYMBOLS.BULLET') }}
                  {{ $t('SGP.FORMS.CEP') }} {{ item.cep }}
                </div>
              </li>
            </template>
          </ul>
        </div>
        <div class="grid grid-cols-2 gap-2">
          <Input
            ref="numberInputRef"
            v-model="ctoForm.numero"
            :label="`${$t('SGP.FORMS.NUMBER')} ${$t('SGP.SYMBOLS.ASTERISK')}`"
            :message="ctoErrors.numero"
            message-type="error"
            @update:model-value="clearCtoError('numero')"
          />
          <Input
            v-model="ctoForm.bairro"
            :label="`${$t('SGP.FORMS.DISTRICT')} (${$t('SGP.FORMS.OPTIONAL')})`"
          />
        </div>
        <div class="grid grid-cols-2 gap-2">
          <Input
            v-model="ctoForm.latitude"
            label="Latitude"
            @update:model-value="markManualCoords"
          />
          <Input
            v-model="ctoForm.longitude"
            label="Longitude"
            @update:model-value="markManualCoords"
          />
        </div>
        <Input
          v-model="ctoForm.coordenada"
          :label="$t('SGP.FORMS.COORD')"
          @update:model-value="markManualCoords"
        />
        <div class="flex items-center gap-2 text-xs text-n-slate-11">
          <button
            type="button"
            class="px-3 py-2 rounded border border-n-weak text-sm text-n-slate-12 hover:border-n-slate-10"
            :disabled="geocodeLoading"
            @click="geocodeFromAddress()"
          >
            {{ geocodeLoading ? $t('SGP.LOADING') : $t('SGP.PRE.GEOLOCATE') }}
          </button>
          <span v-if="geocodeError" class="text-n-ruby-11">
            {{ geocodeError }}
          </span>
        </div>
        <div class="grid grid-cols-2 gap-2">
          <Input
            v-model="ctoForm.cidade"
            :label="`${$t('SGP.FORMS.CITY')} (${$t('SGP.FORMS.OPTIONAL')})`"
          />
          <Input
            v-model="ctoForm.uf"
            :label="`${$t('SGP.FORMS.STATE')} (${$t('SGP.FORMS.OPTIONAL')})`"
          />
        </div>
        <div v-if="viabilityCtoResult">
          <div v-if="viabilityCtoResult.error" class="text-sm text-n-ruby-11">
            {{ viabilityCtoResult.message || $t('SGP.RESULTS.ERROR_GENERIC') }}
          </div>
          <div
            v-else-if="Array.isArray(viabilityCtoResult?.[0]?.SGPMaps)"
            class="space-y-3"
          >
            <div class="text-sm font-medium text-n-slate-12">
              {{
                $t('SGP.RESULTS.CTO_FOUND_COUNT', {
                  count: viabilityCtoResult[0].SGPMaps.length,
                })
              }}
              {{ $t('CONVERSATION.DIALOGS.LOCATION_CHECK.FOUND') }}
            </div>
            <div class="max-h-48 overflow-y-auto space-y-2">
              <div
                v-for="(cto, idx) in viabilityCtoResult[0].SGPMaps"
                :key="idx"
                class="bg-n-solid-2/30 rounded-lg p-3 border border-n-weak"
              >
                <div class="flex items-start justify-between gap-3">
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-medium text-n-slate-12 truncate">
                      {{ cto.nome }}
                    </div>
                    <div class="text-xs text-n-slate-11 mt-1">
                      {{ $t('CONVERSATION.DIALOGS.LOCATION_CHECK.DISTANCE') }}
                      {{ cto.distancia?.toFixed?.(0) || cto.distancia
                      }}{{ $t('CONVERSATION.DIALOGS.LOCATION_CHECK.METERS') }}
                    </div>
                    <div class="text-xs text-n-slate-10 mt-0.5">
                      {{ cto.lat?.toFixed?.(6) || cto.lat
                      }}{{ $t('SGP.SYMBOLS.COMMA') }}
                      {{ cto.lng?.toFixed?.(6) || cto.lng }}
                    </div>
                  </div>
                  <div class="flex-shrink-0">
                    <span
                      class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                      :class="{
                        'bg-n-teal-3 text-n-teal-11': cto.distancia <= 100,
                        'bg-n-amber-3 text-n-amber-11':
                          cto.distancia > 100 && cto.distancia <= 300,
                        'bg-n-ruby-3 text-n-ruby-11': cto.distancia > 300,
                      }"
                    >
                      {{
                        cto.distancia <= 100
                          ? $t('CONVERSATION.DIALOGS.LOCATION_CHECK.IDEAL')
                          : cto.distancia <= 300
                            ? 'Viável'
                            : 'Distante'
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="preFlowSource === 'cto' && isCtoViable(viabilityCtoResult)"
              class="pt-2"
            >
              <button
                type="button"
                class="px-3 py-2 rounded bg-n-brand text-white text-sm hover:opacity-90"
                @click="proceedPreFromViability"
              >
                {{ $t('SGP.PRE_FLOW.CONTINUE_PRE') }}
              </button>
            </div>
          </div>
          <div
            v-else-if="
              Array.isArray(viabilityCtoResult) && !viabilityCtoResult.length
            "
            class="text-sm text-n-slate-11"
          >
            {{ $t('SGP.RESULTS.VIABILITY_NOK') }}
          </div>
          <div
            v-else-if="typeof viabilityCtoResult?.[0]?.SGPMaps === 'string'"
            class="text-sm text-n-slate-11"
          >
            {{ viabilityCtoResult[0].SGPMaps }}
          </div>
          <div
            v-else-if="viabilityCtoResult?.msg"
            class="text-sm text-n-slate-11"
          >
            {{ viabilityCtoResult.msg }}
          </div>
          <div v-else class="text-sm text-n-slate-11">
            {{ $t('CONVERSATION.DIALOGS.LOCATION_CHECK.UNEXPECTED_RESULT') }}
          </div>
        </div>
      </div>
    </Dialog>

    <Dialog
      v-if="showPreRegistrationDialog"
      ref="preRef"
      :is-loading="submittingCto"
      @confirm="onPreRegistrationConfirm"
      @close="resetPreDialog"
    >
      <template #title>{{ $t('SGP.DIALOGS.PRE_REGISTRATION.TITLE') }}</template>
      <div class="flex flex-col gap-3">
        <Input
          v-model="ctoForm.cep"
          :label="$t('SGP.FORMS.CEP')"
          placeholder="00000-000"
          @blur="
            () =>
              fetchAndFillCep(
                ctoForm.value,
                (ctoForm.cep || '').replace(/\D+/g, ''),
                ctoCepLookupError,
                lastCtoCepSearched
              )
          "
        />
        <div class="relative">
          <Input
            v-model="ctoForm.endereco"
            :label="`${$t('SGP.FORMS.ADDRESS')} (${$t('SGP.FORMS.OPTIONAL')})`"
            @focus="handleCtoAddressFocus"
            @blur="handleCtoAddressBlur"
          />
          <ul
            v-if="ctoAutocompleteOpen"
            class="absolute z-50 mt-1 w-full max-h-52 overflow-auto rounded-xl bg-n-alpha-3 backdrop-blur-[100px] outline outline-1 outline-n-container shadow-lg text-sm"
          >
            <template v-if="ctoAutocompleteLoading">
              <li class="px-3 py-2 text-n-slate-11">
                {{ $t('SGP.LOADING') }}
              </li>
            </template>
            <template v-else>
              <li
                v-for="item in ctoAutocompleteResults"
                :key="`${item.cep}-${item.logradouro}-${item.bairro}`"
                class="cursor-pointer px-3 py-2 hover:bg-n-alpha-2"
                @mousedown.prevent="selectCtoAddressSuggestion(item)"
              >
                <div class="text-n-slate-12">
                  {{ item.logradouro }}
                </div>
                <div class="text-xs text-n-slate-11">
                  {{ item.bairro }}
                  {{ $t('SGP.SYMBOLS.BULLET') }}
                  {{ item.localidade }}{{ $t('SGP.SYMBOLS.SLASH')
                  }}{{ item.uf }}
                  {{ $t('SGP.SYMBOLS.BULLET') }}
                  {{ $t('SGP.FORMS.CEP') }} {{ item.cep }}
                </div>
              </li>
            </template>
          </ul>
        </div>
        <div class="grid grid-cols-2 gap-2">
          <Input
            ref="numberInputRef"
            v-model="ctoForm.numero"
            :label="`${$t('SGP.FORMS.NUMBER')} ${$t('SGP.SYMBOLS.ASTERISK')}`"
            :message="ctoErrors.numero"
            message-type="error"
            @update:model-value="clearCtoError('numero')"
          />
          <Input
            v-model="ctoForm.bairro"
            :label="`${$t('SGP.FORMS.DISTRICT')} (${$t('SGP.FORMS.OPTIONAL')})`"
          />
        </div>
        <div class="grid grid-cols-2 gap-2">
          <Input
            v-model="ctoForm.latitude"
            label="Latitude"
            @update:model-value="markManualCoords"
          />
          <Input
            v-model="ctoForm.longitude"
            label="Longitude"
            @update:model-value="markManualCoords"
          />
        </div>
        <Input
          v-model="ctoForm.coordenada"
          label="Coordenada (lat,long)"
          @update:model-value="markManualCoords"
        />
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">{{
            $t('SGP.FORMS.RADIUS')
          }}</label>
          <select
            v-model="ctoForm.raio"
            class="w-full !rounded !bg-n-surface-1 !px-3 !py-2 !text-sm text-n-slate-12 !outline-n-weak hover:!outline-n-slate-6 focus:!outline-n-slate-7 !mb-0 !h-auto"
          >
            <option value="100">{{ $t('SGP.DISTANCE_100M') }}</option>
            <option value="300">{{ $t('SGP.DISTANCE_300M') }}</option>
            <option value="500">{{ $t('SGP.DISTANCE_500M') }}</option>
            <option value="1000">{{ $t('SGP.DISTANCE_1000M') }}</option>
          </select>
        </div>

        <div v-if="preViabilityResult">
          <div v-if="preViabilityResult.error" class="text-sm text-n-ruby-11">
            {{ $t('SGP.RESULTS.ERROR_GENERIC') }}
          </div>
          <div
            v-else-if="
              Array.isArray(preViabilityResult) &&
              preViabilityResult[0]?.SGPMaps?.length
            "
            class="text-sm text-n-slate-12"
          >
            {{
              $t('SGP.RESULTS.CTO_FOUND_COUNT', {
                count: preViabilityResult[0].SGPMaps.length,
              })
            }}
          </div>
          <div v-else class="text-sm text-n-slate-11">
            {{ $t('SGP.RESULTS.VIABILITY_NOK') }}
          </div>
        </div>
      </div>
    </Dialog>

    <Dialog
      v-if="showScheduleInstallDialog"
      ref="scheduleRef"
      @close="() => (showScheduleInstallDialog = false)"
    >
      <template #title>{{ $t('SGP.DIALOGS.SCHEDULE_INSTALL.TITLE') }}</template>
      <template #default>
        <div class="p-2 text-sm text-n-subtle">
          {{ $t('SGP.DIALOGS.SCHEDULE_INSTALL.DESCRIPTION') }}
        </div>
      </template>
    </Dialog>
  </div>
</template>
